import type { Post } from "@repo/fika-shared/src/loader/post-schema.js"
import { useNavigate } from "@solidjs/router"
import { createMemo, createSignal, Show } from "solid-js"
import { useAppContext } from "@/context/app"
import { Form } from "@/designSystem/components/form"
import { Icon } from "@/designSystem/components/icon"
import { Switch } from "@/designSystem/components/switch"
import { Well } from "@/designSystem/components/well"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { Navigation } from "@/designSystem/patterns/navigation"
import { Section } from "@/designSystem/patterns/section"
import { useI18nContext } from "@/i18n/i18n-solid"
import { getTitle } from "@/lib/getTitle"
import { createBlogRouter } from "@/routes/posts-router"
import { alertClass, formClass } from "./post-publish.css"
import usePost from "./use-post"

export function PostPublish() {
  const post = usePost()

  return <Show when={post()}>{post => <SafePostPublish post={post()} />}</Show>
}

function SafePostPublish(props: { post: Post }) {
  const { db, session, replicache } = useAppContext()
  const navigate = useNavigate()
  const [formEmail, setFormEmail] = createSignal(true)
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()
  const title = createMemo(() => getTitle(props.post?.title))
  const buttons = createMemo(() => {
    return [
      {
        label: LL().common.blog(),
        href: paths().url,
      } as const,
      {
        label: title(),
        href: paths().post(props.post).url,
      } as const,
    ]
  })

  const followers = createMemo(() => {
    const publisher = db.publisher.findBy({ user_id: session.userID })
    if (!publisher) return []

    return db.subscription.filterBy({ publisher_id: publisher.id })
  })

  async function publish() {
    const date = new Date().toISOString()
    await replicache.mutate.publishPost({
      id: props.post.id,
      published_at: date,
      sent_at: formEmail() ? date : null,
    })
    navigate(paths().post(props.post).url)
  }

  return (
    <MainLayout
      header={<Navigation hue="amber" buttons={buttons()} title="publish" />}
    >
      <form
        class={formClass}
        onSubmit={e => {
          e.preventDefault()
          e.stopPropagation()
          publish()
        }}
      >
        <Section
          title="Delivery"
          description="How do you want to deliver this post?"
          hue="amber"
          iconName="send"
        >
          <Well hue="sand">
            <Switch
              label="Send via email"
              hue="amber"
              checked={formEmail()}
              onChange={setFormEmail}
            />
          </Well>
        </Section>
        <Show when={formEmail()}>
          <div class={alertClass}>
            <Icon name="mail" color="amber600" size="24" />
            Your {followers().length} followers will receive this post on their
            inbox.
          </div>
        </Show>
        <Form.Save
          hue={formEmail() ? "indigo" : "emerald"}
          iconName="send"
          label={formEmail() ? "publish & send" : "publish"}
        />
      </form>
    </MainLayout>
  )
}
